import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { User, Search, X, FileText } from 'lucide-react';

const SearchView = () => {
  const { users, posts, currentUser, setView, viewUserProfile, wordFilters, searchNav, setSearchNav } = useContext(AppContext);
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchNav) {
      setActiveTab(searchNav.tab || 'users');
      setQuery(searchNav.query || '');
      if (setSearchNav) setSearchNav(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchNav && inputRef.current) inputRef.current.focus();
  }, [searchNav]);

  const trimmedQuery = query.trim().toLowerCase().replace(/^@/, '');

  const filteredUsers = trimmedQuery.length >= 1 ? users.filter(u =>
    u.id !== currentUser.id &&
    u.role !== 'admin' &&
    u.role !== 'superadmin' &&
    (u.name?.toLowerCase().includes(trimmedQuery) ||
     u.firstName?.toLowerCase().includes(trimmedQuery) ||
     u.lastName?.toLowerCase().includes(trimmedQuery) ||
     u.handle?.toLowerCase().includes(trimmedQuery))
  ).slice(0, 20) : [];

  const filteredPosts = useMemo(() =>
    trimmedQuery.length >= 2 ? posts.filter(p =>
      p.content?.toLowerCase().includes(trimmedQuery)
    ).slice(0, 20) : [],
    // Only recalculate when the query changes, not on every posts update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trimmedQuery, posts.length]
  );

  // Blur input when scrolling to prevent mobile browsers from snapping back to focused input
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    const container = scrollContainerRef.current?.closest('main');
    if (!container) return;
    const handleScroll = () => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={scrollContainerRef} className="max-w-2xl mx-auto p-4">
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a] focus:border-transparent"
          placeholder={activeTab === 'users' ? 'Search users by name or handle...' : 'Search posts...'}
        />
        {query && (
          <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-4 bg-gray-900 rounded-lg p-1">
        <button onClick={() => setActiveTab('users')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'users' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>
          <span className="flex items-center justify-center space-x-1"><User className="w-4 h-4" /><span>Users</span></span>
        </button>
        <button onClick={() => setActiveTab('posts')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'posts' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>
          <span className="flex items-center justify-center space-x-1"><FileText className="w-4 h-4" /><span>Posts</span></span>
        </button>
      </div>

      {/* Results */}
      {activeTab === 'users' && (
        <div>
          {!trimmedQuery ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-400">Search for users by name or @handle</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No users found for "{query}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map(user => (
                <button key={user.id} onClick={() => viewUserProfile(user.id)} className="flex items-center space-x-3 w-full p-3 hover:bg-gray-900 rounded-lg transition-colors text-left">
                  <img src={user.profilePicture} alt={user.name} className="w-12 h-12 rounded-full" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{user.name}</p>
                    <p className="text-sm text-gray-400 truncate">@{user.handle}</p>
                  </div>
                  {user.skillLevel && (
                    <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded-full">{user.skillLevel}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'posts' && (
        <div>
          {trimmedQuery.length < 2 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-400">Type at least 2 characters to search posts</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No posts found for "{query}"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPosts.map(post => {
                const author = users.find(u => u.id === post.userId);
                return (
                  <button key={post.id} onClick={() => { setView('feed'); setTimeout(() => { const el = document.getElementById(`post-${post.id}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300); }} className="w-full bg-black border border-gray-700 rounded-lg p-4 text-left hover:border-[#16a34a] transition-colors">
                    <div className="flex items-center space-x-2 mb-2">
                      {author && <img src={author.profilePicture} alt={author.name} className="w-8 h-8 rounded-full" />}
                      <div>
                        <p className="text-white text-sm font-medium">{author?.name}</p>
                        <p className="text-xs text-gray-400">@{author?.handle}</p>
                      </div>
                    </div>
                    <p className="text-white text-sm line-clamp-3">{post.content}</p>
                    <p className="text-xs text-gray-500 mt-2">{post.createdAt ? new Date(post.createdAt.seconds ? post.createdAt.seconds * 1000 : post.createdAt).toLocaleDateString() : ''}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchView;
